<template>
  <v-container
   :class="isRepriseTab ? 'pa-0' : ''"
  >
    <v-navigation-drawer
      v-model="drawer"
      app
      width="320"
      class="
        hide-overflow
        z4
      "
      :class="[{'disable':loading}]"
    >
      <div 
        class="z2 no-scroll"
        :class="isRepriseTab ? 'pa-3 full-height' : 'height-85 pa-5 light-grey-bg'"
      >
        <v-row 
          justify="center"
          :no-gutters="isRepriseTab"
        >
          <v-date-picker
            v-if="!isInstructorMobile"
            :class="{ 'outline-1 rounded-lg': isRepriseTab }"
            :width="isRepriseTab ? '100%' : 230"
            :picker-date.sync="pickerDate"
            :first-day-of-week="1"
            :locale-first-day-of-year="4"
            :no-title="true"
            :value="selectedDate"
            :show-week="true"
            @input="changeDate"
          />
        </v-row>
        <PlannerFilters v-if="!isRepriseTab" />
        <v-row 
          v-if="!isInstructorMobile"
          :no-gutters="isRepriseTab"
        >
          <RepriseFilters v-if="isRepriseTab" />
          <Filters v-else/>
        </v-row>
        <v-row v-else>
          <v-col cols="12">
            <div class="full-height">
              <WorkspacePicker />
            </div>
          </v-col>
        </v-row>
      </div>
      <template v-if="!isRepriseTab && !isInstructorMobile">
        <v-divider />
        <div class="d-flex flex-column height-15 align-center justify-center">
          <v-switch v-model="debugSwitch" color="third" label="Mode débogage" />
          <v-btn v-if="debugMode" color="third white--text" @click="openReportModal">
            <span>Signaler un bug</span>
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <Modal
      v-if="showReportModal"
      :show="showReportModal"
      max-width="768"
      :close="closeReportModal"
      :show-dividers="false"
      persistent
    >
      <template #title>
        <div class="d-flex col-12 justify-center third white--text">
          <span>Page de rapport de bogue</span>
        </div>
      </template>
      <template #text>
        <div class="d-flex flex justify-center">
          <v-form v-model="reportValid" class="col-12">
            <v-textarea
              v-model="reportDescription"
              outlined
              name="report-bug-textarea"
              label="Décrire le problème"
              class="mt-2"
              color="third"
              :rules="reportRules"
            />
          </v-form>
        </div>
      </template>
      <template #actions>
        <div class="d-flex col-12 justify-end rounded-lg">
          <v-btn class="third white--text mr-2" outlined :disabled="!reportValid" :loading="reportLoading" @click="createBugReport">
            <span>Soumettre le bug</span>
          </v-btn>
          <v-btn outlined @click="closeReportModal">
            <span>Annuler</span>
          </v-btn>
        </div>
      </template>
    </Modal>
  </v-container>
</template>

<script>
import { ref, computed, watch, watchEffect } from '@vue/composition-api';
import store from '@/store';

import { decompress } from 'compress-json';
import { handleSessions } from '../../helpers/amplify';
import { EventBus, isSameWeek, isSameMonth, sendNotification, filterObjectsAttrsByKeys, storeAttributesForBugReport } from '@/helpers/utils';
import { createReportUploadUrl, uploadReportToS3 } from '@/helpers/request';

import Filters from '../filters/Filters.vue';
import RepriseFilters from '../reprises/filters/RepriseFilters.vue';
import PlannerFilters from '../filters/PlannerFilters.vue';
import Modal from '@/components/common/Modal.vue';
import WorkspacePicker from '@/components/instructor/sideBar/WorkspacePicker.vue';

export default {
  components: { Filters, WorkspacePicker, RepriseFilters, PlannerFilters, Modal },
  setup () {
    const drawer = ref(false);
    const loading = computed(() => store.state.loading);
    const selectedDate = computed(() => store.getters.selectedDate);
    const isInstructorMobile = computed(() => store.getters.isIntructorOnMobile);
    const currentTab = computed(() => store.state.currentTab);
    const debugMode = computed(() => store.state.debugMode);
    const debugSwitch = ref(debugMode.value);

    const pickerDate = ref(new Date().toISOString().substr(0, 10));

    const canShowTreatmentSwitch = computed(() => currentTab.value === 'customer');
    const isRepriseTab = computed(() => currentTab.value === 'reprise');

    const showTreatmentSessions = ref((store.state.showSessionsOfType || []).includes('SOINS'));
    const reportDescription = ref(null);
    const reportValid = ref(false);
    const showReportModal = ref(false);
    const reportLoading = ref(false);

    const openReportModal = () => {
      showReportModal.value = true;
    };

    const closeReportModal = () => {
      showReportModal.value = false;
    };

    const resetReport = () => {
      reportDescription.value = null;
      closeReportModal();
    };

    const createBugReport = async () => {
      reportLoading.value = true;

      const response = await createReportUploadUrl();
      if (!(response && response.success && response.data && response.data.url && response.data.date)) {
        sendNotification('error', response.message);
        reportLoading.value = false;
      }

      const storeToSend = filterObjectsAttrsByKeys(store.state, storeAttributesForBugReport);

      const uploadResponse = await uploadReportToS3(`${response.data.url}`, { date: response.data.date, description: reportDescription.value, store: { ...storeToSend, networkLogs: (storeToSend.networkLogs || []).map(compressedLog => decompress(JSON.parse(JSON.stringify(compressedLog)))) } });
      reportLoading.value = false;
      if (uploadResponse.success) {
        resetReport();
      }
      sendNotification(uploadResponse.success ? 'success' : 'error', uploadResponse.message);
    };

    const reportRules = ref([
      value => !!value || 'Les champs ne peuvent pas être vides, veuillez décrire le problème.'
    ]);

    const changeShowTreatmentSession = () => {
      const types = store.state.showSessionsOfType;

      if (showTreatmentSessions.value) {
        types.push('SOINS');
      } else {
        const index = types.findIndex(t => t === 'SOINS');

        if (index !== -1) {
          types.splice(index, 1);
        }
      }

      store.commit('setShowSessionsOfType', types);
    };

    watch(debugMode, newVal => {
      if (newVal !== debugSwitch.value) {
        debugSwitch.value = newVal;
      }
    });

    watch(debugSwitch, newVal => {
      if (newVal !== debugMode.value) {
        store.commit('setDebugMode', newVal);
      }
    });

    watch(showReportModal, newVal => {
      if (newVal) {
        drawer.value = !newVal;
      }
    });

    const canShowMonitorTypeSessions = computed(() => currentTab.value !== 'instructor');

    const showMonitorTypeSessions = ref(false);

    const monitorTypeValues = ['FORMATION', 'ADMINISTRATIF', 'ABSENCE', 'REUNION', 'PERMANENCE'];

    watchEffect(() => {
      const sessionTypes = store.state.showSessionsOfType;
      if (sessionTypes) {
        showMonitorTypeSessions.value = monitorTypeValues.every(value => sessionTypes.includes(value));
      } else {
        showMonitorTypeSessions.value = false;
      }
    });

    const changeShowMonitorTypeSessions = () => {
      const sessionTypes = store.state.showSessionsOfType;

      if (showMonitorTypeSessions.value) {
        sessionTypes.push(...monitorTypeValues);
      } else {
        monitorTypeValues.forEach(value => {
          const index = sessionTypes.findIndex(t => t === value);

          if (index !== -1) {
            sessionTypes.splice(index, 1);
          }
        });
      }

      store.commit('setShowSessionsOfType', sessionTypes);
    };

    const changeDate = async value => {
      const sameWeek = isSameWeek(value);
      const sameMonth = isSameMonth(value);
      store.commit('setSelectedDate', value);

      if (!sameWeek) {
        store.commit('setLoading', true);
        await handleSessions();
        store.commit('setLoading', false);
      }

      if (!sameMonth) {
        store.commit('setLoading', true);
        await store.dispatch('allMonthlySessions', 'horses');
        store.commit('setLoading', false);
      }
    };

    EventBus.$on('changeDrawerState', () => {
      drawer.value = !drawer.value;
    });

    watch(drawer, val => {
      store.commit('setSidebarOpen', val);
    }, { immediate: true });

    EventBus.$on('closeDrawerState', () => {
      drawer.value = false;
    });

    return {
      changeShowMonitorTypeSessions,
      showMonitorTypeSessions,
      canShowMonitorTypeSessions,
      canShowTreatmentSwitch,
      isRepriseTab,
      changeShowTreatmentSession,
      showTreatmentSessions,
      loading,
      drawer,
      selectedDate,
      pickerDate,
      changeDate,
      isInstructorMobile,
      debugMode,
      debugSwitch,
      showReportModal,
      openReportModal,
      closeReportModal,
      createBugReport,
      reportDescription,
      reportRules,
      reportValid,
      reportLoading
    };
  }
};
</script>

<style>
  .hide-overflow > .v-navigation-drawer__content {
    overflow: hidden;
  }
</style>
